import * as React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';

import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SellIcon from '@mui/icons-material/Sell';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import BarChartIcon from '@mui/icons-material/BarChart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

const DRAWER_WIDTH = 264;
const MINI_WIDTH = 76;
const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

const DrawerPanel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: open ? DRAWER_WIDTH : MINI_WIDTH,
  overflow: 'hidden',
  background: theme.palette.mode === 'light'
    ? `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.055)} 100%)`
    : theme.palette.background.paper,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const Shell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  display: 'flex',
  minHeight: '100dvh',
  background: theme.palette.background.default,
  '& .app-content': {
    flex: 1,
    minWidth: 0,
    height: '100dvh',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(2),
    marginLeft: open ? DRAWER_WIDTH : MINI_WIDTH,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.up('md')]: { padding: theme.spacing(3) },
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      padding: theme.spacing(1.5),
    },
  },
}));

const navSections = [
  {
    title: 'Operación',
    items: [
      { to: '/inicio', label: 'Inicio', icon: <DashboardIcon /> },
      { to: '/ventas', label: 'Ventas', icon: <SellIcon /> },
      { to: '/ordenes_compra', label: 'Órdenes de compra', icon: <AssignmentIcon /> },
      { to: '/reportes', label: 'Reportes', icon: <BarChartIcon /> },
      { to: '/finanzas', label: 'Finanzas', icon: <AccountBalanceWalletIcon /> },
      { to: '/transacciones', label: 'Transacciones', icon: <SwapHorizIcon /> },
    ],
  },
  {
    title: 'Inventario',
    items: [
      { to: '/productos/carga-masiva-imagenes', label: 'Carga masiva de imágenes', icon: <AddPhotoAlternateIcon />, adminOnly: true },
      { to: '/productos', label: 'Productos', icon: <Inventory2Icon />, exact: true },
      { to: '/etiquetas-barras', label: 'Etiquetas de barras', icon: <QrCodeScannerIcon /> },
      { to: '/verificaciones-stock', label: 'Verificación de stock', icon: <AssignmentIcon /> },
      { to: '/categorias', label: 'Categorías', icon: <CategoryIcon /> },
      { to: '/marcas', label: 'Marcas', icon: <BrandingWatermarkIcon /> },
    ],
  },
  {
    title: 'Contactos y envío',
    items: [
      { to: '/clientes', label: 'Clientes', icon: <PeopleIcon /> },
      { to: '/proveedores', label: 'Proveedores', icon: <StoreIcon /> },
      { to: '/transportistas', label: 'Transportistas', icon: <LocalShippingIcon /> },
      { to: '/encomendistas', label: 'Encomendistas', icon: <DirectionsTransitIcon /> },
    ],
  },
];

const allItems = navSections.flatMap((section) => section.items);

export default function Layout({ toggleTheme, mode }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = React.useContext(AuthContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const currentPage = allItems.find((item) => (
    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
  ));
  const displayName = user?.username || user?.nombre || 'Usuario';
  const userRole = user?.rol || (user?.rol_id === 1 ? 'Administrador' : 'Cuenta');
  const initial = String(displayName).trim().charAt(0).toUpperCase() || 'U';

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout?.();
    navigate('/login');
  };

  const isActive = (item) => item.exact
    ? location.pathname === item.to
    : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  const drawerContent = (expanded) => (
    <DrawerPanel open={expanded}>
      <Box sx={{ px: expanded ? 2 : 1.25, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color: 'primary.contrastText',
              background: (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
              boxShadow: (t) => `0 8px 18px ${alpha(t.palette.primary.main, 0.25)}`,
            }}
          >
            <Inventory2Icon />
          </Box>
          {expanded && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" noWrap>
                Inventario
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Centro operativo
              </Typography>
            </Box>
          )}
          {expanded && !isMobile && (
            <Tooltip title="Contraer menú">
              <IconButton size="small" onClick={() => setOpen(false)} aria-label="Contraer menú">
                {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Divider />

      <List sx={{ px: 1, py: 1.5, flex: 1, overflowY: 'auto' }}>
        {navSections.map((section, sectionIndex) => (
          <Box key={section.title} sx={{ mb: sectionIndex === navSections.length - 1 ? 0 : 1.25 }}>
            {expanded && (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', px: 1.5, pb: 0.65, fontSize: '0.63rem' }}
              >
                {section.title}
              </Typography>
            )}
            {section.items
              .filter((item) => !item.adminOnly || user?.rol_id === 1 || user?.rol === 'admin')
              .map((item) => {
              const active = isActive(item);
              const button = (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    position: 'relative',
                    minHeight: 44,
                    mx: 0.5,
                    mb: 0.35,
                    px: expanded ? 1.25 : 1.5,
                    justifyContent: expanded ? 'initial' : 'center',
                    borderRadius: 2.5,
                    color: active ? 'primary.main' : 'text.secondary',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 9,
                      bottom: 9,
                      width: 3,
                      borderRadius: '0 4px 4px 0',
                      bgcolor: 'primary.main',
                      opacity: active ? 1 : 0,
                    },
                    '&.Mui-selected': {
                      bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.1 : 0.22),
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.14 : 0.28),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: expanded ? 38 : 0, color: 'inherit', justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  {expanded && <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: active ? 750 : 600 }} />}
                </ListItemButton>
              );

              return expanded ? button : (
                <Tooltip key={item.to} title={item.label} placement="right">
                  {button}
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </List>

      <Divider />
      <Box sx={{ p: 1 }}>
        {expanded && (
          <Stack direction="row" alignItems="center" spacing={1.1} sx={{ px: 1, py: 0.75 }}>
            <Avatar sx={{ width: 30, height: 30, fontSize: '0.78rem', bgcolor: 'secondary.main' }}>{initial}</Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={700} noWrap>{displayName}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{userRole}</Typography>
            </Box>
          </Stack>
        )}
        <Tooltip title={expanded ? '' : 'Cerrar sesión'} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 42,
              borderRadius: 2.25,
              color: 'error.main',
              justifyContent: expanded ? 'initial' : 'center',
              px: expanded ? 1.25 : 1.5,
              '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
            }}
          >
            <ListItemIcon sx={{ minWidth: expanded ? 38 : 0, color: 'inherit', justifyContent: 'center' }}>
              <LogoutIcon />
            </ListItemIcon>
            {expanded && <ListItemText primary="Cerrar sesión" primaryTypographyProps={{ fontWeight: 700 }} />}
          </ListItemButton>
        </Tooltip>
      </Box>
    </DrawerPanel>
  );

  return (
    <Shell open={!isMobile && open}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          ml: { md: open ? `${DRAWER_WIDTH}px` : `${MINI_WIDTH}px` },
          width: { md: open ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_WIDTH}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, md: 68 }, gap: 1.25 }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {!isMobile && !open && (
            <Tooltip title="Expandir menú">
              <IconButton onClick={() => setOpen(true)} aria-label="Expandir menú">
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              {currentPage?.label || 'Sistema de Inventario'}
            </Typography>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.35 }}>
              Control de operaciones
            </Typography>
          </Box>

          <Tooltip title={mode === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}>
            <IconButton onClick={toggleTheme} aria-label={mode === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Administrar usuarios">
            <IconButton onClick={() => navigate('/usuarios')} aria-label="Administrar usuarios">
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.82rem', bgcolor: 'secondary.main' }}>{initial}</Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box
        component={Paper}
        elevation={0}
        square
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100dvh',
          zIndex: (t) => t.zIndex.drawer,
          border: 0,
          borderRight: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: 'none',
        }}
      >
        {drawerContent(open)}
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 0 },
        }}
      >
        {drawerContent(true)}
      </Drawer>

      <Box className="app-content" component="main">
        <Offset />
        <Outlet />
      </Box>
    </Shell>
  );
}
